import { Container, Alert, Button } from 'react-bootstrap';
import { useAuth } from '@context';
import { hasCapability, hasAnyCapability, hasAllCapabilities } from '@utils/permissions';
import PropTypes from 'prop-types';

/**
 * RequireCapability Component
 * Restricts access based on specific capabilities
 * More flexible than role-based checks
 */
export function RequireCapability({ 
  children, 
  required = [], 
  requireAll = true,
  fallback = 'Unauthorized: Insufficient permissions.'
}) {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!user) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="mb-3">
          {fallback}
        </Alert>
        <div className="text-center">
          <Button variant="primary" href="/login">Go to Login</Button>
        </div>
      </Container>
    );
  }

  // Handle single capability
  if (typeof required === 'string') {
    if (!hasCapability(user, required)) {
      return (
        <Container className="py-5">
          <Alert variant="danger" className="mb-3">
            {fallback}
          </Alert>
          <div className="text-center">
            <Button variant="primary" href="/">Return Home</Button>
          </div>
        </Container>
      );
    }
  }
  // Handle multiple capabilities
  else if (Array.isArray(required) && required.length > 0) {
    const hasAccess = requireAll 
      ? hasAllCapabilities(user, required)
      : hasAnyCapability(user, required);

    if (!hasAccess) {
      return (
        <Container className="py-5">
          <Alert variant="danger" className="mb-3">
            {fallback}
          </Alert>
          <div className="text-center">
            <Button variant="primary" href="/">Return Home</Button>
          </div>
        </Container>
      );
    }
  }

  return children;
}

RequireCapability.propTypes = {
  children: PropTypes.node.isRequired,
  required: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  requireAll: PropTypes.bool,
  fallback: PropTypes.string,
};
